import ExperimentCard from "./ExperimentCard";

// Track metadata used for grouping + visual section banners. Shared by
// /reports/:token (paid report) and /connected (free teaser) so the two
// surfaces present experiments with the same sectioned UX.
export const TRACK_SECTIONS = [
  {
    key: "foundation",
    progressKey: "foundation",
    title: "Foundation Audit",
    subtitle: "On-page fundamentals per URL — title, meta, H1, schema, content depth. For small or new sites.",
    accent: "border-sky-400 bg-sky-50 text-sky-900",
    leftBar: "bg-sky-400",
    icon: "🧱",
  },
  {
    key: "long-tail",
    progressKey: "long_tail",
    title: "Long-tail Capture",
    subtitle: "Queries ranking at position 20–100. Small tweaks to break into the top 10.",
    accent: "border-lime-400 bg-lime-50 text-lime-900",
    leftBar: "bg-lime-400",
    icon: "🎯",
  },
  {
    key: "ctr-fix",
    progressKey: "ctr_fix",
    title: "CTR Fixes",
    subtitle: "Pages bleeding impressions — rewrite title + meta to recover clicks.",
    accent: "border-amber-400 bg-amber-50 text-amber-900",
    leftBar: "bg-amber-400",
    icon: "⚡",
  },
  {
    key: "new-page",
    progressKey: "new_pages",
    title: "New Page Opportunities",
    subtitle: "Queries you rank for without a dedicated page. Build these next.",
    accent: "border-indigo-400 bg-indigo-50 text-indigo-900",
    leftBar: "bg-indigo-400",
    icon: "🗺️",
  },
  {
    key: "content-refresh",
    progressKey: "content_refresh",
    title: "Content Refresh",
    subtitle: "Pages that used to rank and are quietly decaying. Update and revive.",
    accent: "border-teal-400 bg-teal-50 text-teal-900",
    leftBar: "bg-teal-400",
    icon: "🔄",
  },
  {
    key: "protect",
    progressKey: "protect",
    title: "Protect Rankings",
    subtitle: "Slipping pages worth defending.",
    accent: "border-rose-400 bg-rose-50 text-rose-900",
    leftBar: "bg-rose-400",
    icon: "🛡️",
  },
  {
    key: "conversion",
    progressKey: "conversion",
    title: "Conversion Levers",
    subtitle: "High-traffic pages with weak CTAs — turn visits into action.",
    accent: "border-purple-400 bg-purple-50 text-purple-900",
    leftBar: "bg-purple-400",
    icon: "🎯",
  },
];

export default function ExperimentsByTrack({
  experiments = [],
  progress = {},
  isProcessing = false,
  showSummaryBar = true,
  emptyState = null,
}) {
  const buckets = {};
  for (const exp of experiments) {
    const t = exp.track || "other";
    (buckets[t] ||= []).push(exp);
  }

  const canonical = TRACK_SECTIONS.map((s) => s.key);
  const extraKeys = Object.keys(buckets).filter((k) => !canonical.includes(k));
  const sections = [
    ...TRACK_SECTIONS,
    ...extraKeys.map((k) => ({
      key: k, progressKey: k,
      title: k.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      subtitle: "", accent: "border-slate-300 bg-slate-50 text-slate-800",
      leftBar: "bg-slate-400", icon: "•",
    })),
  ];

  const highPriorityCount = experiments.filter((e) => e.priority === "high").length;

  if (experiments.length === 0 && !Object.keys(progress || {}).some((k) => k.endsWith("_total") && progress[k] > 0)) {
    return emptyState ?? (
      <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-sm text-slate-500">
        Warming up the sonar. First experiments show up here in a minute or two.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showSummaryBar && experiments.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div>
            <span className="text-slate-400 text-xs uppercase tracking-wide">Total</span>
            <span className="ml-2 font-extrabold text-slate-900">{experiments.length}</span>
          </div>
          {highPriorityCount > 0 && (
            <div>
              <span className="text-slate-400 text-xs uppercase tracking-wide">High priority</span>
              <span className="ml-2 font-extrabold text-red-600">{highPriorityCount}</span>
            </div>
          )}
          {sections.filter((s) => (buckets[s.key] || []).length > 0).map((s) => (
            <div key={s.key}>
              <span className="text-slate-400 text-xs uppercase tracking-wide">{s.title}</span>
              <span className="ml-2 font-extrabold text-slate-900">{buckets[s.key].length}</span>
            </div>
          ))}
          {isProcessing && (
            <div className="ml-auto text-xs text-slate-500">New experiments appear as they finish…</div>
          )}
        </div>
      )}

      {sections.map((section) => {
        const items = (buckets[section.key] || []).slice().sort((a, b) => (b.score || 0) - (a.score || 0));
        const total = progress?.[`${section.progressKey}_total`];
        const state = progress?.[section.progressKey];
        const showSection = items.length > 0 || (typeof total === "number" && total > 0);
        if (!showSection) return null;

        return (
          <section key={section.key} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
              <div className={`w-1 self-stretch rounded-full ${section.leftBar}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base">{section.icon}</span>
                  <h3 className="text-base font-extrabold text-slate-900 uppercase tracking-wide">
                    {section.title}
                  </h3>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded border ${section.accent}`}>
                    {items.length}
                    {typeof total === "number" && total > items.length && ` / ${total}`}
                  </span>
                  {state === "running" && (
                    <span className="text-[11px] text-indigo-600 font-semibold">Generating…</span>
                  )}
                  {state === "failed" && (
                    <span className="text-[11px] text-red-600 font-semibold">Some failed</span>
                  )}
                </div>
                {section.subtitle && (
                  <p className="text-xs text-slate-500 mt-1">{section.subtitle}</p>
                )}
              </div>
            </div>

            {items.length > 0 ? (
              <div className="p-5 space-y-4">
                {items.map((exp, i) => (
                  <ExperimentCard key={exp.id || exp.url || i} exp={exp} />
                ))}
              </div>
            ) : (
              <div className="px-5 py-6 text-sm text-slate-500">
                {state === "running" ? (
                  <>Generating {total} experiments… results stream in as they finish.</>
                ) : (
                  <>No {section.title.toLowerCase()} found in your GSC data.</>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
